import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
  Divider,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import Grid from "@mui/material/Grid2";
import { useNavigate } from "react-router-dom";
import Layout from "../components/layout/Layout";
import generateMembership from "../components/membership/generateMembership";
import { useEffect, useRef, useState } from "react";
import SnackError from "../components/Signups/SnackError";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import WorkIcon from "@mui/icons-material/Work";

function MembershipTile({ icon, title, subtitle, features, selected, dimmed, onSelect, children }) {
  return (
    <Card
      role="radio"
      aria-checked={selected}
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect();
        }
      }}
      sx={{
        width: "100%",
        height: "100%",
        cursor: "pointer",
        borderRadius: 3,
        border: "3px solid",
        borderColor: selected ? "secondary.main" : "transparent",
        backgroundColor: dimmed ? "grey.200" : "background.paper",
        opacity: dimmed ? 0.75 : 1,
        boxShadow: selected
          ? "0 8px 24px rgba(0,0,0,0.15)"
          : "0 4px 12px rgba(0,0,0,0.1)",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
          opacity: 1,
        },
      }}
    >
      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 48,
              height: 48,
              flexShrink: 0,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.06),
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography
              variant="h5"
              component="h2"
              sx={{ fontWeight: 600, color: "text.primary", lineHeight: 1.2 }}
            >
              {title}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {subtitle}
            </Typography>
          </Box>
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          {features.map((feature) => (
            <Typography
              key={feature}
              component="li"
              variant="body2"
              sx={{
                color: "text.primary",
                py: 0.25,
                "&::marker": { color: "primary.main" },
              }}
            >
              {feature}
            </Typography>
          ))}
        </Box>

        {children}
      </CardContent>
    </Card>
  );
}

function MembershipSelectionPage({
  devPort,
  currentMembership,
  setCurrentMembership,
  userName,
  setUserName,
}) {
  let navigate = useNavigate();
  const [selectedMembership, setSelectedMembership] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alterMessage, setAlertMessage] = useState(false);
  const onboardingStarted = useRef(false);
  useEffect(() => {
    if (userName && !onboardingStarted.current){
      if (currentMembership === "manager") {
        navigate("/club");
      } else if (currentMembership === "player") {
        navigate("/playerclubselection");
      }
    }
  }, [userName, currentMembership, navigate]);

  function handleNext() {
    onboardingStarted.current = true;
    if (selectedMembership === "player") {
      setIsSubmitting(true);
      generateMembership(devPort, userName, 0, setCurrentMembership).then(() =>
        navigate("/onboarding/player")
      );
    } else if (selectedMembership === "manager") {
      navigate("/onboarding/manager");
    }
  }

  return (
    <Layout managerMode={-2} devPort={devPort}>
      <SnackError
        alterMessage={alterMessage}
        setAlertMessage={setAlertMessage}
        snackMessage="This username was not found"
      />
      <Box sx={{ maxWidth: 900, mx: "auto", px: 2, py: 3 }}>
        <Typography
          variant="h5"
          component="h1"
          sx={{
            textAlign: "center",
            mb: 3,
            fontWeight: 600,
            color: "white",
          }}
        >
          Select your membership
        </Typography>

        <Grid container spacing={2} justifyContent="center" role="radiogroup">
          <Grid size={{ xs: 12, md: 6 }} sx={{ display: "flex" }}>
            <MembershipTile
              icon={<PersonOutlineIcon sx={{ color: "text.primary" }} />}
              title="Player"
              subtitle="Select to participate in club events"
              features={[
                "Join clubs",
                "Check schedules",
                "Register for games, lessons, and tournaments",
              ]}
              selected={selectedMembership === "player"}
              dimmed={selectedMembership === "manager"}
              onSelect={() => setSelectedMembership("player")}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }} sx={{ display: "flex" }}>
            <MembershipTile
              icon={<WorkIcon sx={{ color: "text.primary" }} />}
              title="Club Manager"
              subtitle="Select to host and manage club events"
              features={[
                "Create your club",
                "Manage games, player sign-ups",
                "Subscription based on club size",
              ]}
              selected={selectedMembership === "manager"}
              dimmed={selectedMembership === "player"}
              onSelect={() => setSelectedMembership("manager")}
            />
          </Grid>
        </Grid>

        <Button
          onClick={handleNext}
          variant="contained"
          size="large"
          fullWidth
          disabled={!selectedMembership || isSubmitting}
          sx={{
            mt: 3,
            py: 1.5,
            fontSize: "1rem",
            fontWeight: 600,
            borderRadius: 2,
            textTransform: "none",
            backgroundColor: "#fff",
            color: "text.primary",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            "&:hover": {
              backgroundColor: "grey.100",
              boxShadow: "0 6px 16px rgba(0,0,0,0.2)",
            },
            "&.Mui-disabled": {
              backgroundColor: "rgba(255,255,255,0.85)",
              color: "rgba(0,0,0,0.4)",
            },
          }}
        >
          Next
        </Button>
      </Box>
    </Layout>
  );
}

export default MembershipSelectionPage;
